/** Shared types and helpers for RichTextField (short) and RichTextEditor (document). */

import type { ReactNode } from 'react'

export type RichTextEditorMode = 'preview' | 'markdown';

export type RichTextChrome = 'field' | 'document';

/** Bordered field control vs borderless artifact canvas surface. */
export type RichTextFrame = 'field' | 'canvas';

/** Where Preview | Markdown lives — canvas defaults to artifact header. */
export type RichTextModeSwitcher = 'inline' | 'header' | 'none';

export const RICH_TEXT_VISUAL_SERIALIZE_DEBOUNCE_MS = 150;

/** 24px horizontal, 0 top, 12px bottom — Plate surface in `frame="canvas"` only. */
export const RICH_TEXT_CANVAS_PADDING_CLASS = 'px-6 pt-0 pb-3';

export type RichTextInputProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  'aria-invalid'?: boolean;
  className?: string;
  /** `canvas` — full-pane editing in artifact canvases (borderless, pinned char count). */
  frame?: RichTextFrame;
  mode?: RichTextEditorMode;
  onModeChange?: (mode: RichTextEditorMode) => void;
  /**
   * `inline` — switcher above the editor (field default).
   * `header` — switcher in artifact canvas header (canvas default).
   * `none` — hide switcher.
   */
  modeSwitcher?: RichTextModeSwitcher;
  /**
   * Optional leading content for the inline switcher row (e.g. a field label).
   * Renders on the same row as Preview | Markdown — title left, switcher right.
   */
  modeSwitcherLeading?: ReactNode;
  /** Override default canvas horizontal padding (`px-6`). Use `px-0` for page-aligned layouts. */
  canvasContentClassName?: string;
  /** Live visible character count for artifact footers (Preview semantics in both modes). */
  onCharCountChange?: (count: number) => void;
};

/**
 * Commit editor markdown to parent state.
 *
 * Always sync — including when over `maxLength`. Plate Preview can show pasted
 * overages (red char count); blocking onChange left parents with a stale empty
 * value and false "required" errors on save. Length is enforced on submit.
 */
export function clampRichTextChange(
  next: string,
  _maxLength: number | undefined,
  onChange: (value: string) => void,
) {
  onChange(next);
}

export function resolveRichTextModeSwitcher(
  frame: RichTextFrame,
  modeSwitcher: RichTextModeSwitcher | undefined,
): RichTextModeSwitcher {
  return modeSwitcher ?? (frame === 'canvas' ? 'header' : 'inline');
}

/** True when parent `value` changed from outside this editor (agent / API), not from our last emit. */
export function shouldApplyExternalRichTextValue(
  incomingValue: string,
  lastEmittedValue: string,
): boolean {
  return incomingValue !== lastEmittedValue
}
